import { Link, useNavigate } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { PostureLink } from '@/components/PostureIndicator'
import { HorizontalScrollArea } from '@/components/HorizontalScrollArea'
import { DEVICE_COLUMNS } from '@/config/columns'
import {
  formatCpuSpeed,
  formatLastSeen,
  formatOs,
  formatRam,
} from '@/lib/formatAgent'

const COLUMN_CLASS = {
  hostname: 'min-w-[120px]',
  display_name: 'min-w-[140px]',
  customer: 'min-w-[120px]',
  os_name: 'min-w-[180px]',
  os_build: 'min-w-[80px]',
  machine_type: 'min-w-[90px]',
  cpu_name: 'min-w-[160px]',
  cpu_cores: 'min-w-[60px]',
  ram_mb: 'min-w-[72px]',
  domain: 'min-w-[100px]',
  last_logged_user: 'min-w-[100px]',
  primary_contact_name: 'min-w-[140px]',
  primary_contact_email: 'min-w-[160px]',
  agent_version: 'min-w-[80px]',
  online_status: 'min-w-[80px]',
  security_status: 'min-w-[110px]',
  hardware_status: 'min-w-[110px]',
  toolkit_status: 'min-w-[110px]',
  last_seen: 'min-w-[130px] whitespace-nowrap',
  last_reboot_time: 'min-w-[130px] whitespace-nowrap',
  windows_serial_number: 'min-w-[140px]',
  folder_name: 'min-w-[120px]',
  app_view_url: 'min-w-[90px]',
  computer_description: 'min-w-[160px]',
  reported_from_ip: 'min-w-[110px]',
  system_drive: 'min-w-[72px]',
  created_at_atera: 'min-w-[130px] whitespace-nowrap',
  modified_at_atera: 'min-w-[130px] whitespace-nowrap',
  av_product: 'min-w-[140px]',
  av_status: 'min-w-[110px]',
}

const AV_STATUS_LABELS = {
  protected: 'Protected',
  not_detected: 'Not detected',
  no_data: 'No software data',
}

function AvStatusBadge({ status }) {
  if (!status || status === 'no_data') {
    return <span className="text-muted-foreground">{AV_STATUS_LABELS.no_data}</span>
  }
  if (status === 'protected') {
    return <Badge variant="success">{AV_STATUS_LABELS.protected}</Badge>
  }
  if (status === 'not_detected') {
    return <Badge variant="destructive">{AV_STATUS_LABELS.not_detected}</Badge>
  }
  return <span className="capitalize">{status.replace(/_/g, ' ')}</span>
}

function SortableHeader({ column, label, className, sortBy, sortDir, onSort }) {
  const active = sortBy === column
  return (
    <th
      className={cn(
        'px-3 py-3 font-medium cursor-pointer select-none hover:text-foreground',
        className,
      )}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" />
        )}
      </span>
    </th>
  )
}

function DeviceNameLink({ agentId, children, className }) {
  if (!agentId) {
    return <span className={className}>{children}</span>
  }
  return (
    <Link
      to={`/agents/${agentId}`}
      className={cn('text-primary hover:underline', className)}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </Link>
  )
}

function CellContent({ column, agent }) {
  switch (column.id) {
    case 'hostname':
      return (
        <DeviceNameLink agentId={agent.atera_id} className="font-medium">
          {agent.hostname || `Device ${agent.atera_id}`}
        </DeviceNameLink>
      )
    case 'display_name':
      return agent.display_name ? (
        <DeviceNameLink agentId={agent.atera_id}>
          {agent.display_name}
        </DeviceNameLink>
      ) : (
        '—'
      )
    case 'customer':
      return agent.customer_id && agent.customer_name ? (
        <Link
          to={`/customers/${agent.customer_id}`}
          className="text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {agent.customer_name}
        </Link>
      ) : (
        agent.customer_name || '—'
      )
    case 'os_name':
      return (
        <span className="block max-w-[220px] truncate" title={formatOs(agent)}>
          {formatOs(agent)}
        </span>
      )
    case 'os_build':
      return agent.os_build || '—'
    case 'machine_type':
      return <span className="capitalize">{agent.machine_type || '—'}</span>
    case 'cpu_name': {
      const speed = formatCpuSpeed(agent.cpu_speed_mhz)
      const title = [agent.cpu_name, speed].filter(Boolean).join(' · ')
      return (
        <span className="block max-w-[200px] truncate" title={title}>
          {agent.cpu_name || '—'}
          {speed && (
            <span className="block text-xs text-muted-foreground">{speed}</span>
          )}
        </span>
      )
    }
    case 'cpu_cores':
      return agent.cpu_cores ?? '—'
    case 'ram_mb':
      return formatRam(agent.ram_mb)
    case 'domain':
      return agent.domain || '—'
    case 'last_logged_user':
      return agent.last_logged_user || '—'
    case 'primary_contact_name':
      return agent.primary_contact_name || '—'
    case 'primary_contact_email':
      return agent.primary_contact_email ? (
        <a
          href={`mailto:${agent.primary_contact_email}`}
          className="text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {agent.primary_contact_email}
        </a>
      ) : (
        '—'
      )
    case 'agent_version':
      return agent.agent_version || '—'
    case 'online_status':
      return (
        <Badge variant={agent.online_status ? 'success' : 'warning'}>
          {agent.online_status ? 'Online' : 'Offline'}
        </Badge>
      )
    case 'security_status':
      return (
        <PostureLink
          agentId={agent.atera_id}
          status={agent.security_status}
          tab="security"
          label="Security"
        />
      )
    case 'hardware_status':
      return (
        <PostureLink
          agentId={agent.atera_id}
          status={agent.hardware_status}
          tab="hardware"
          label="Hardware"
        />
      )
    case 'toolkit_status':
      return (
        <PostureLink
          agentId={agent.atera_id}
          status={agent.toolkit_status}
          tab="overview"
          label="Toolkit"
        />
      )
    case 'last_seen':
      return (
        <span className="text-muted-foreground">{formatLastSeen(agent.last_seen)}</span>
      )
    case 'last_reboot_time':
      return (
        <span className="text-muted-foreground">{formatLastSeen(agent.last_reboot_time)}</span>
      )
    case 'windows_serial_number':
      return (
        <span className="block max-w-[140px] truncate font-mono text-xs" title={agent.windows_serial_number}>
          {agent.windows_serial_number || '—'}
        </span>
      )
    case 'folder_name':
      return agent.folder_name || '—'
    case 'app_view_url':
      return agent.app_view_url ? (
        <a
          href={agent.app_view_url}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          Open
        </a>
      ) : (
        '—'
      )
    case 'computer_description':
      return (
        <span className="block max-w-[200px] truncate" title={agent.computer_description}>
          {agent.computer_description || '—'}
        </span>
      )
    case 'reported_from_ip':
      return agent.reported_from_ip || '—'
    case 'system_drive':
      return agent.system_drive || '—'
    case 'created_at_atera':
      return (
        <span className="text-muted-foreground">{formatLastSeen(agent.created_at_atera)}</span>
      )
    case 'modified_at_atera':
      return (
        <span className="text-muted-foreground">{formatLastSeen(agent.modified_at_atera)}</span>
      )
    case 'av_product':
      return agent.av_product || '—'
    case 'av_status':
      return <AvStatusBadge status={agent.av_status} />
    default:
      return '—'
  }
}

function AgentRow({ agent, columnsWithClass }) {
  const navigate = useNavigate()

  return (
    <tr
      className="cursor-pointer border-b border-border/60 transition-colors hover:bg-muted/20"
      onClick={() => {
        if (agent.atera_id != null) {
          navigate(`/agents/${agent.atera_id}`)
        }
      }}
    >
      {columnsWithClass.map((col) => (
        <td key={col.id} className={cn('px-3 py-2.5', col.className)}>
          <CellContent column={col} agent={agent} />
        </td>
      ))}
    </tr>
  )
}

export function AgentsTable({
  agents,
  sortBy = 'hostname',
  sortDir = 'asc',
  onSort,
  hideCustomer = false,
  visibleColumnIds,
}) {
  const available = DEVICE_COLUMNS.filter(
    (c) => !(hideCustomer && c.hideForCustomer),
  )
  const byId = Object.fromEntries(available.map((c) => [c.id, c]))
  const visibleColumns = visibleColumnIds
    ? visibleColumnIds.map((id) => byId[id]).filter(Boolean)
    : available

  const columnsWithClass = visibleColumns.map((c) => ({
    ...c,
    className: COLUMN_CLASS[c.id] || '',
  }))

  if (!visibleColumns.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Select at least one column to display.
      </p>
    )
  }

  if (!agents.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">No devices found.</p>
    )
  }

  return (
    <HorizontalScrollArea>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            {columnsWithClass.map((col) => (
              <SortableHeader
                key={col.id}
                column={col.id}
                label={col.label}
                className={col.className}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {agents.map((agent) => (
            <AgentRow
              key={agent.atera_id}
              agent={agent}
              columnsWithClass={columnsWithClass}
            />
          ))}
        </tbody>
      </table>
    </HorizontalScrollArea>
  )
}
